<script setup>
  import {computed} from "vue";

  import {area} from "@pages/Records/Components/Result/Enums.js";
  import PagesRecordResultListChecks from "@pages/Records/Components/Result/ResultList/ListChecks.vue";

  defineOptions({
    name: "PagesRecordResultSymptomaticResults",
  });

  const props = defineProps({
    patientId: {
      type: String,
      required: true,
    },
    recordId: {
      type: String,
      required: true,
    },
    symptoms: {
      type: Array,
      default: () => {
        return [];
      },
    },
    symptomaticRedlights: {
      type: Array,
      default: () => {
        return [];
      },
    },
  });

  defineEmits(["open:shareDialog"]);

  const medicationBasedSymptomaticRedlights = computed(() => {
    return props.symptomaticRedlights.filter((check) => check.basis === "medication");
  });

  const diseaseBasedSymptomaticRedlights = computed(() => {
    return props.symptomaticRedlights.filter((check) => check.basis === "disease");
  });

  const laborvalueBasedSymptomaticRedlights = computed(() => {
    return props.symptomaticRedlights.filter((check) => check.basis === "laborvalue");
  });
</script>

<template>
  <section
    v-if="
      medicationBasedSymptomaticRedlights.length > 0 ||
      diseaseBasedSymptomaticRedlights.length > 0 ||
      laborvalueBasedSymptomaticRedlights.length > 0
    "
    :data-test="getTestId('symptomatic-results')"
  >
    <h2 class="border-b-1 border-gray-200 pb-6 text-center text-2xl font-semibold print:font-normal">
      Probleme / Symptome
      <span class="font-light">(aktuelle)</span>

      <div v-if="symptoms.length > 0" class="font-light text-mcred-500">
        {{ symptoms.map((symptom) => symptom.symptom).join(", ") }}
      </div>
    </h2>

    <div v-if="medicationBasedSymptomaticRedlights.length > 0" class="mt-8 space-y-4 print:break-before-avoid-page">
      <h3 class="text-xl font-semibold">Ursachen mit Arzneimittelbezug</h3>
      <PagesRecordResultListChecks
        class="print:break-before-avoid-page"
        anchor-prefix="symptomatic"
        :patient-id="patientId"
        :record-id="recordId"
        :checks="medicationBasedSymptomaticRedlights"
        :area="area.symptomatic"
        :test-id="getTestId('symptomatic-results-medication')"
        @open:share-dialog="$emit('open:shareDialog')"
      />
    </div>

    <div v-if="diseaseBasedSymptomaticRedlights.length > 0" class="mt-8 space-y-4">
      <h3 class="text-xl font-semibold">Ursachen mit Erkrankungsbezug</h3>
      <PagesRecordResultListChecks
        class="print:break-before-avoid-page"
        anchor-prefix="symptomatic"
        :patient-id="patientId"
        :record-id="recordId"
        :checks="diseaseBasedSymptomaticRedlights"
        :area="area.symptomatic"
        :test-id="getTestId('symptomatic-results-disease')"
        @open:share-dialog="$emit('open:shareDialog')"
      />
    </div>

    <div v-if="laborvalueBasedSymptomaticRedlights.length > 0" class="mt-8 space-y-4">
      <h3 class="text-xl font-semibold">Ursachen mit Laborwertbezug</h3>
      <PagesRecordResultListChecks
        class="print:break-before-avoid-page"
        anchor-prefix="symptomatic"
        :patient-id="patientId"
        :record-id="recordId"
        :checks="laborvalueBasedSymptomaticRedlights"
        :area="area.symptomatic"
        :test-id="getTestId('symptomatic-results-labor-value')"
        @open:share-dialog="$emit('open:shareDialog')"
      />
    </div>
  </section>
</template>
